import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is your typical project timeline?",
      answer: "Project timelines vary based on scope and complexity. A simple landing page might take 2-3 weeks, while a full-stack web application typically requires 2-4 months. During our initial consultation, I'll provide a detailed timeline based on your specific requirements and current workload."
    },
    {
      question: "Do you work with startups or only established companies?",
      answer: "I work with both! I have extensive experience with growth-stage startups and understand the unique challenges of building MVPs quickly while maintaining scalability. I also partner with established companies and agencies on larger projects. Budget flexibility and clear communication matter more than company size."
    },
    {
      question: "What is your pricing structure?",
      answer: "I offer both project-based and hourly rates depending on the engagement type. Project-based pricing provides cost certainty for defined scopes, while hourly rates work better for ongoing maintenance or evolving requirements. After understanding your needs, I'll provide a detailed proposal with transparent pricing and payment milestones."
    },
    {
      question: "Do you provide ongoing support after project completion?",
      answer: "Yes! All projects include a 30-day warranty period for bug fixes. I also offer monthly retainer packages for ongoing maintenance, feature additions, and technical support. Many clients choose to continue working with me after launch to ensure their application stays current and performs optimally."
    },
    {
      question: "What technologies do you specialize in?",
      answer: "My core stack includes React, Node.js, Next.js, and modern JavaScript/TypeScript. I'm proficient in both SQL and NoSQL databases, cloud platforms (AWS, Vercel, Netlify), and various APIs and third-party integrations. I choose technologies based on project requirements rather than personal preference."
    },
    {
      question: "How do you handle project communication?",
      answer: "Clear communication is crucial. I provide weekly progress updates via email, maintain a shared project management board (Trello/Notion), and schedule bi-weekly video calls for larger projects. I'm available via email during business hours and respond within 24 hours. For urgent issues, active clients have access to priority support."
    },
    {
      question: "Can you work with my existing development team?",
      answer: "Absolutely! I frequently collaborate with in-house teams and other contractors. I'm comfortable with code reviews, pair programming, and following established coding standards. I can also provide technical leadership and architecture guidance if needed."
    },
    {
      question: "What if I need changes after the project is completed?",
      answer: "Minor adjustments during the 30-day warranty period are included. For larger changes or new features, I offer flexible options: hourly billing for small updates, or we can scope out a new project phase. Many clients find monthly retainer packages most cost-effective for ongoing development needs."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8 md:mb-10 lg:mb-12">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
          Quick answers to common questions about working together. Don't see your question? Feel free to reach out directly.
        </p>
      </div>
      <div className="space-y-4">
        {faqs?.map((faq, index) => (
          <div
            key={index}
            className="rounded-lg bg-card border border-border overflow-hidden transition-all duration-300 hover:border-primary/50"
          >
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between gap-4 p-4 md:p-5 lg:p-6 text-left transition-colors duration-300 hover:bg-muted/50"
            >
              <h3 className="text-base md:text-lg font-bold font-heading text-foreground pr-4">
                {faq?.question}
              </h3>
              <div className={`flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                <Icon name="ChevronDown" size={24} className="text-primary" />
              </div>
            </button>
            
            <div
              className={`overflow-hidden transition-all duration-300 ${
                openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 md:p-5 lg:p-6 pt-0 text-sm md:text-base text-muted-foreground leading-relaxed">
                {faq?.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 p-6 md:p-8 rounded-lg bg-primary/10 border border-primary/20">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/20 flex-shrink-0">
            <Icon name="HelpCircle" size={28} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold font-heading text-foreground mb-2">
              Still have questions?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-4">
              I'm happy to discuss your specific situation and answer any questions you might have about working together.
            </p>
            <a
              href="mailto:contact@mikedev.pro"
              className="inline-flex items-center gap-2 text-sm md:text-base font-medium text-primary hover:underline"
            >
              <Icon name="Mail" size={18} />
              <span>Send me an email</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQSection;